import React, {
  useState, createContext, useContext, useMemo, useEffect,
} from 'react';
import { useAppDispatch } from 'core/store';
import { HTTPService } from 'core/services';
import useNotifiers from 'shared/Notifier/UseNotifiers';
import periodMapping from 'shared/PeriodAutocomplete/constants';
import { useActionsPermissions } from 'core/hooks/useActionsPermissions';
import calculatePeriod from 'core/utilities/dateUtilities/calculatePeriod';
import { PERIOD_SELECT } from 'shared/PeriodAutocomplete/PeriodAutocomplete.models';
import { TPeriod, TSetPeriod } from 'shared/CustomRangePicker/CustomRangePicker.models';
import { ACTIONS_DASHBOARD_ESCALATION_SUPPORT_PATHS } from 'core/models/actionsPermissions';
import { notifierMessage } from '@dashboardEscalationSupport/EscalationSupportCharts/constants';
import { getEscalationSupportTotalAction } from '@dashboardEscalationSupport/EscalationSupportCharts/Charts/Total/GetEscalationSupportTotal';
import {
  getEscalationSupportEscalationTypeAction,
} from '@dashboardEscalationSupport/EscalationSupportCharts/Charts/EscalationType/GetEscalationSupportEscalationType';

const { TOTAL_CHART_GET, ESCALATION_TYPE_CHART_GET } = ACTIONS_DASHBOARD_ESCALATION_SUPPORT_PATHS;

const actionsPaths = {
  isAvailableTotalChart: TOTAL_CHART_GET,
  isAvailableEscalationTypeChart: ESCALATION_TYPE_CHART_GET,
};

interface IActionsPermissions {
  isAvailableTotalChart: boolean;
  isAvailableEscalationTypeChart: boolean;
}

interface IEscalationSupportChartsContext {
  period: TPeriod;
  getEscalationSupportData: TSetPeriod;
  actionsPermissions: IActionsPermissions;
}

interface IEscalationSupportChartsProviderProps {
  children: React.ReactNode;
}

const EscalationSupportChartsContext = createContext<IEscalationSupportChartsContext>(
  {} as IEscalationSupportChartsContext,
);

let escalationSupportTotalController = HTTPService.getController();
let escalationSupportEscalationTypeController = HTTPService.getController();

const EscalationSupportChartsProvider: React.FC<
  IEscalationSupportChartsProviderProps
> = ({ children }) => {
  const defaultPeriod = calculatePeriod(periodMapping, PERIOD_SELECT.ONE_MONTH);
  const [period, setPeriod] = useState(defaultPeriod);

  const dispatch = useAppDispatch();

  const { showErrorNotifier } = useNotifiers();

  const actionsPermissions = useActionsPermissions<IActionsPermissions>(actionsPaths);
  const {
    isAvailableTotalChart,
    isAvailableEscalationTypeChart,
  } = actionsPermissions;

  const cancelGetEscalationSupportTotalRequest = () => {
    HTTPService.cancelRequest(escalationSupportTotalController);
  };

  const cancelGetEscalationSupportEscalationTypeRequest = () => {
    HTTPService.cancelRequest(escalationSupportEscalationTypeController);
  };

  const cancelRequests = () => {
    cancelGetEscalationSupportTotalRequest();
    cancelGetEscalationSupportEscalationTypeRequest();
  };

  const getEscalationSupportTotal = async (newPeriod: TPeriod) => {
    if (!isAvailableTotalChart) return;
    cancelGetEscalationSupportTotalRequest();

    escalationSupportTotalController = HTTPService.getController();
    try {
      await dispatch(getEscalationSupportTotalAction({
        controller: escalationSupportTotalController, params: newPeriod,
      })).unwrap();
    } catch (e) {
      showErrorNotifier(notifierMessage.escalationSupportTotal.error, e);
    }
  };

  const getEscalationSupportEscalationType = async (newPeriod: TPeriod) => {
    if (!isAvailableEscalationTypeChart) return;
    cancelGetEscalationSupportEscalationTypeRequest();

    escalationSupportEscalationTypeController = HTTPService.getController();
    try {
      await dispatch(getEscalationSupportEscalationTypeAction({
        controller: escalationSupportEscalationTypeController, params: newPeriod,
      })).unwrap();
    } catch (e) {
      showErrorNotifier(notifierMessage.escalationSupportType.error, e);
    }
  };

  const getEscalationSupportData = (newPeriod: TPeriod) => {
    getEscalationSupportTotal(newPeriod);
    getEscalationSupportEscalationType(newPeriod);
    setPeriod(newPeriod);
  };

  useEffect(() => {
    getEscalationSupportData(period);

    return () => cancelRequests();
  }, []);

  const value = useMemo(() => ({
    period,
    actionsPermissions,
    getEscalationSupportData,
  }), [period, actionsPermissions]);

  return (
    <EscalationSupportChartsContext.Provider value={value}>
      {children}
    </EscalationSupportChartsContext.Provider>
  );
};

export const useEscalationSupportChartsContext = () => useContext(EscalationSupportChartsContext);

export default EscalationSupportChartsProvider;
